import { AlertTriangle } from 'lucide-react'
import { Badge } from '@/components/ui/badge'
import {
  type CallFacet,
  formatWait,
  isRejectedWithoutACallStage,
  LANE_LABELS,
  STAGE_LABELS,
} from '@/lib/audition/queue'
import type { AuditionStage } from '@/lib/audition/state-machine'

/**
 * The admin-facing vocabulary, in one place (LG-414 — see CONTEXT.md).
 *
 * Every word here is deliberate. "Audition" and "vibe call" are code-only
 * names; an admin reads "Applied", "Call booked" and "intro call". The two
 * lanes are "Pay-first" and "Talk-first" — the db enum keeps `fast` and
 * `consideration`, which describe nothing to a human.
 */

export function stageLabel(stage: AuditionStage): string {
  return STAGE_LABELS[stage]
}

export function laneLabel(lane: keyof typeof LANE_LABELS | null): string {
  return lane ? LANE_LABELS[lane] : 'Path not chosen yet'
}

export function StageTag({ stage }: { stage: AuditionStage }) {
  // `client` is the destination and `rejected` is the end of the road, so
  // neither should draw the eye the way a live stage does.
  const variant =
    stage === 'client'
      ? 'default'
      : stage === 'rejected' || stage === 'wizard_in_progress'
        ? 'secondary'
        : 'outline'
  return <Badge variant={variant}>{stageLabel(stage)}</Badge>
}

/**
 * Paid and Call are facts about an applicant that their stage cannot express
 * — a `payment_unlocked` applicant may have had a call, been let in without
 * one, or no-showed one — so they ride alongside the stage rather than inside
 * it.
 */
export function PaidTag() {
  return (
    <Badge className="bg-emerald-600 text-white" title="Payment received">
      PAID
    </Badge>
  )
}

/**
 * One label per call fact, shared by the chip on a row and the filter pill
 * that selects rows carrying it, so the word an admin clicks is the word they
 * then see on every match. `none` has no chip (below) and gets its label at
 * the pill; `rejected_no_call` has one reading the chip departs from on a
 * live row ({@link callLabel}).
 */
export const CALL_LABELS: Record<Exclude<CallFacet, 'none'>, string> = {
  offered: 'Call offered',
  scheduled: 'Call scheduled',
  held: 'Call held',
  no_show: 'No-showed',
  accepted_no_call: 'Accepted w/o a call',
  rejected_no_call: 'Rejected w/o a call',
}

const CALL_VARIANTS: Record<
  Exclude<CallFacet, 'none'>,
  'outline' | 'secondary' | 'destructive'
> = {
  offered: 'outline',
  scheduled: 'outline',
  held: 'outline',
  // Their miss, so it is loud; deciding without a call was our own choice,
  // so it is not.
  no_show: 'destructive',
  accepted_no_call: 'secondary',
  rejected_no_call: 'secondary',
}

/**
 * The word a row's chip wears for its call fact, given where the row is.
 *
 * One bucket, two histories: `rejected_no_call` gathers "refused without a
 * call" and "the offer was withdrawn", and the pill names it by the decision
 * because on the queue page that is what the filter is for. But a live row
 * whose only offer was withdrawn has no decision yet — its owed column still
 * says "Decide" — and a chip reading "Rejected" beside it is a verdict nobody
 * gave. So until the row IS rejected, the chip says what happened instead.
 *
 * Which stages count as "rejected" is `callFacet`'s own question, asked here
 * rather than answered again: spelling it `stage !== 'rejected'` would be a
 * second copy of that list, exact only for as long as the list has one entry.
 */
export function callLabel(
  call: Exclude<CallFacet, 'none'>,
  stage: AuditionStage,
): string {
  if (call === 'rejected_no_call' && !isRejectedWithoutACallStage(stage))
    return 'Offer withdrawn'
  return CALL_LABELS[call]
}

export function CallTag({
  call,
  stage,
}: {
  call: CallFacet
  stage: AuditionStage
}) {
  // Nothing to say about a call that has not entered the picture yet — a chip
  // reading "no call" on every fresh application is noise.
  if (call === 'none') return null
  return <Badge variant={CALL_VARIANTS[call]}>{callLabel(call, stage)}</Badge>
}

/**
 * How long they have been waiting, and the date that age was measured from.
 *
 * An overdue row says so in words rather than only in colour — the whole
 * point of the flag is that it survives a quick scan, and colour alone does
 * not survive a screenshot, a colourblind reviewer, or a printed page.
 *
 * The date is RENDERED, not hidden behind hover. On the queue this is the
 * only place the applied date exists, and a `title` on a non-focusable span
 * reaches nobody on a keyboard, a touch screen, or a screen reader — so
 * "when did this person apply" was unanswerable on an iPad, on the page
 * whose entire subject is how long people have waited. A visible `<time>`
 * answers all three at once, which an `sr-only` span would not (and an
 * `aria-label` cannot: a bare `<span>` has no role to carry one).
 *
 * `appliedAt` must be the same instant `days` was measured from — take both
 * off one `QueueDerivation` rather than pairing a wait age with `createdAt`.
 */
export function WaitLabel({
  days,
  overdue,
  appliedAt,
}: {
  days: number
  overdue: boolean
  appliedAt: Date | string
}) {
  const applied = new Date(appliedAt)
  const wait = overdue ? `Overdue — ${formatWait(days)}` : formatWait(days)
  return (
    <span className="flex flex-col gap-0.5">
      <span
        className={
          overdue
            ? 'text-destructive inline-flex items-center gap-1 text-sm font-medium'
            : 'text-muted-foreground text-sm'
        }
      >
        {overdue && <AlertTriangle aria-hidden="true" className="size-3.5" />}
        {wait}
      </span>
      <time
        dateTime={applied.toISOString()}
        title={applied.toLocaleString()}
        className="text-muted-foreground text-xs"
      >
        {`applied ${applied.toLocaleDateString()}`}
      </time>
    </span>
  )
}
